import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../services/api';
import PackageSelector from '../components/PackageSelector';
import './Dashboard.css';
import '../pages/Register.css';

interface TokenInfo {
    used: number;
    max: number;
    percentage: number;
    expireAt: string;
    isLimitReached: boolean;
}

interface UserPackage {
    id: number;
    name: string;
    name_ar?: string;
    pack_type?: string;
    price: number;
    yearly_price?: number | null;
    max_token: number;
    icon?: string;
    color?: string;
}

interface PaymentMethod {
    id: number;
    name: string;
    nameAr: string;
    icon: string;
}

interface PendingPayment {
    id: number;
    status: string;
    created_at: string;
    package: UserPackage;
    billing_period: string;
}

interface UserData {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    package?: UserPackage;
    package_id?: number;
    billing_period?: 'monthly' | 'annual';
    payment_method_id?: number;
    used_token: number;
    pending_payment?: PendingPayment | null;
    nb_poste?: number | null;
    is_nb_poste_enabled?: boolean;
}

const DashboardSubscription: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Subscription form state
    const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number | null>(null);
    const [nbPoste, setNbPoste] = useState<number | null>(null);
    const [allPackages, setAllPackages] = useState<any[]>([]);
    const [showChargilyRedirect, setShowChargilyRedirect] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [userRes, tokenRes, paymentRes] = await Promise.all([
                    api.get('/user/profile/me'),
                    api.get<TokenInfo>('/user/token-percentage'),
                    api.get('/payment-methods')
                ]);
                setUserData(userRes.data);
                setTokenInfo(tokenRes.data);
                setPaymentMethods(paymentRes.data || []);

                // Set form state from user data
                setSelectedPlan(userRes.data.package_id || null);
                setBillingPeriod(userRes.data.billing_period || 'monthly');
                setSelectedPaymentMethod(userRes.data.payment_method_id || null);
                setNbPoste(userRes.data.nb_poste || null);
            } catch (err) {
                console.error('Error fetching subscription data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleSave = async () => {
        const isChanging = selectedPlan !== userData?.package_id;
        const confirmMsg = isChanging
            ? (isArabic ? 'هل أنت متأكد من تغيير الاشتراك؟' : 'Êtes-vous sûr de vouloir changer de pack ?')
            : (isArabic ? 'هل تريد تجديد اشتراكك الحالي؟' : 'Voulez-vous renouveler votre abonnement actuel ?');

        if (!window.confirm(confirmMsg)) return;

        setSaving(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const selectedPkg = allPackages.find(p => p.id === selectedPlan);
            const res = await api.put('/user/profile/update', {
                package_id: selectedPlan,
                billing_period: billingPeriod,
                payment_method_id: selectedPaymentMethod,
                ...(selectedPkg?.isNbPosteEnabled && nbPoste ? { nb_poste: nbPoste } : {})
            });

            const newPaymentId = res.data?.new_payment_id;

            // Check if "Carte bancaire" is selected → Chargily
            const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
            const isCard = method && (
                method.name.toLowerCase().includes('carte') ||
                method.name.toLowerCase().includes('cib')
            );

            if (isCard && newPaymentId) {
                setShowChargilyRedirect(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });

                const amount = billingPeriod === 'annual' && selectedPkg?.yearlyPrice
                    ? selectedPkg.yearlyPrice
                    : selectedPkg?.price || 0;

                try {
                    const checkoutRes = await api.post('/chargily/checkout', {
                        payment_id: newPaymentId,
                        amount: amount,
                        description: `${isChanging ? 'Changement' : 'Renouvellement'} - ${selectedPkg?.name} - ${userData?.email}`,
                        lang: i18n.language,
                    });
                    if (checkoutRes.data?.checkout_url) {
                        window.location.href = checkoutRes.data.checkout_url;
                        return;
                    }
                } catch (err) {
                    console.error('Chargily checkout error:', err);
                    setShowChargilyRedirect(false);
                }
            }

            setSuccessMessage(isArabic
                ? (isChanging ? 'تم تقديم طلب تغيير الاشتراك بنجاح.' : 'تم تقديم طلب التجديد بنجاح.')
                : (isChanging ? 'Demande de changement enregistrée avec succès.' : 'Demande de renouvellement enregistrée avec succès.')
            );

            const userRes = await api.get('/user/profile/me');
            setUserData(userRes.data);

        } catch (err: any) {
            console.error('Update error:', err);
            if (err.response?.status === 400 && err.response?.data?.detail === 'pending_payment_exists') {
                setErrorMessage(isArabic
                    ? 'لديك طلب دفع قيد الانتظار. يرجى الانتظار حتى تتم معالجته.'
                    : 'Vous avez déjà une demande de paiement en attente.');
            } else {
                setErrorMessage(t('subscription.updateFailed'));
            }
        } finally {
            setSaving(false);
        }
    };

    const usagePercentage = tokenInfo?.percentage || 0;
    const hasPendingPayment = !!userData?.pending_payment;
    const hasChangedPlan = selectedPlan !== null && selectedPlan !== userData?.package_id;
    const isSamePlan = selectedPlan !== null && selectedPlan === userData?.package_id;
    // Always allow: renew same plan or change to different plan
    const canSubmit = selectedPlan !== null;

    return (
        <div className="dashboard-page">
            <div className="container py-4">
                {/* Page Header - centered with icon */}
                <div className="dashboard-header text-center mb-4">
                    <h1 className="dashboard-title justify-content-center">
                        <i className="bi bi-credit-card me-3"></i>
                        {t('subscription.title')}
                    </h1>
                    <p className="text-muted">
                        {t('subscription.subtitle')}
                    </p>
                </div>

                {/* Messages */}
                {successMessage && (
                    <div className="alert alert-success alert-dismissible fade show mb-4">
                        <i className="bi bi-check-circle me-2"></i>
                        {successMessage}
                        <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
                    </div>
                )}
                {errorMessage && (
                    <div className="alert alert-danger alert-dismissible fade show mb-4">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {errorMessage}
                        <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
                    </div>
                )}

                {hasPendingPayment && userData?.pending_payment && (
                    <div className="alert alert-warning mb-4 shadow-sm border-warning">
                        <div className="d-flex align-items-center">
                            <i className="bi bi-clock-history fs-3 me-3"></i>
                            <div>
                                <h5 className="alert-heading mb-1">{t('subscription.pendingRequest', 'Pending Request')}</h5>
                                <p className="mb-0">
                                    {t('subscription.pendingRequestMessage', 'You have a pending request for package:')} <strong>{isArabic ? userData.pending_payment.package.name_ar : userData.pending_payment.package.name}</strong>.
                                    <br />
                                    {t('subscription.pleaseWaitValidation', 'Please wait for administrator validation.')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Usage Section */}
                        <section className="dashboard-section mb-4">
                            <div className="section-header">
                                <h2 className="section-title">
                                    <i className="bi bi-bar-chart me-2 text-primary"></i>
                                    {t('subscription.tokenUsage')}
                                </h2>
                            </div>
                            <div className="section-body">
                                <div className="row align-items-center">
                                    <div className="col-md-6">
                                        <div className="d-flex justify-content-around mb-3">
                                            <div className="text-center">
                                                <div className="fs-2 fw-bold text-primary">{tokenInfo?.used?.toLocaleString() || 0}</div>
                                                <div className="text-muted small">{t('subscription.used')}</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="fs-2 fw-bold text-secondary">{tokenInfo?.max?.toLocaleString() || 0}</div>
                                                <div className="text-muted small">{t('subscription.total')}</div>
                                            </div>
                                        </div>
                                        <div className="progress" style={{ height: '10px' }}>
                                            <div
                                                className="progress-bar bg-primary"
                                                role="progressbar"
                                                style={{ width: `${usagePercentage}%` }}
                                            ></div>
                                        </div>
                                        <div className="mt-2 text-center">
                                            <small className="text-muted">
                                                {(tokenInfo?.max || 0) - (tokenInfo?.used || 0)} {t('subscription.tokensRemaining')}
                                            </small>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        {userData?.package && (
                                            <div className="text-center p-3 bg-light rounded">
                                                <i className={`bi ${userData.package.icon || 'bi-box'} fs-1`} style={{ color: userData.package.color || '#981934' }}></i>
                                                <h5 className="mt-2 mb-1">{isArabic ? userData.package.name_ar : userData.package.name}</h5>
                                                <span className="badge bg-primary">{userData.package.price?.toLocaleString()} DA/{t('subscription.perMonth')}</span>
                                            </div>
                                        )}
                                        {tokenInfo?.isLimitReached && (
                                            <div className="alert alert-warning mt-2 mb-0 py-2">
                                                <i className="bi bi-exclamation-triangle me-1"></i>
                                                {t('subscription.limitReached')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Change Plan Section */}
                        <section className={`dashboard-section mb-4 ${hasPendingPayment ? 'opacity-50 pointer-events-none' : ''}`}>
                            <div className="section-header">
                                <h2 className="section-title">
                                    <i className="bi bi-arrow-repeat me-2 text-success"></i>
                                    {t('subscription.changePlan')}
                                </h2>
                            </div>
                            <div className="section-body">
                                <PackageSelector
                                    selectedPlan={selectedPlan}
                                    onPlanSelect={hasPendingPayment ? () => { } : (planId) => {
                                        setSelectedPlan(planId);
                                        const pkg = allPackages.find(p => p.id === planId);
                                        if (!pkg?.isNbPosteEnabled) setNbPoste(null);
                                    }}
                                    billingPeriod={billingPeriod}
                                    onBillingPeriodChange={hasPendingPayment ? () => { } : setBillingPeriod}
                                    onPackagesLoaded={setAllPackages}
                                />
                            </div>
                        </section>

                        {/* Nombre de postes Section — enterprise packages */}
                        {selectedPlan && allPackages.find(p => p.id === selectedPlan)?.isNbPosteEnabled && (
                            <section className={`dashboard-section mb-4 ${hasPendingPayment ? 'opacity-50 pointer-events-none' : ''}`}>
                                <div className="section-header">
                                    <h2 className="section-title">
                                        <i className="bi bi-pc-display-horizontal me-2 text-warning"></i>
                                        {t('register.nbPoste', 'Nombre de postes')}
                                    </h2>
                                </div>
                                <div className="section-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <label className="form-label">
                                                {t('register.nbPoste', 'Nombre de postes')} <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min={1}
                                                value={nbPoste || ''}
                                                onChange={(e) => setNbPoste(e.target.value ? Math.max(1, parseInt(e.target.value)) : null)}
                                                placeholder={t('register.nbPostePlaceholder', 'Ex: 5, 10, 20...')}
                                                disabled={hasPendingPayment}
                                            />
                                            <small className="text-muted">
                                                {t('register.nbPosteHelp', 'Nombre de connexions simultanées autorisées pour votre entreprise')}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* Payment Method Section */}
                        <section className={`dashboard-section mb-4 ${hasPendingPayment ? 'opacity-50 pointer-events-none' : ''}`}>
                            <div className="section-header">
                                <h2 className="section-title">
                                    <i className="bi bi-wallet2 me-2 text-info"></i>
                                    {t('subscription.paymentMethod')}
                                </h2>
                            </div>
                            <div className="section-body">
                                <div className="row g-3">
                                    {paymentMethods.map((method) => (
                                        <div key={method.id} className="col-md-4 col-6">
                                            <div
                                                className={`payment-card ${selectedPaymentMethod === method.id ? 'selected' : ''}`}
                                                onClick={() => !hasPendingPayment && setSelectedPaymentMethod(method.id)}
                                            >
                                                <i className={`bi ${method.icon} fs-3`}></i>
                                                <span>{isArabic ? method.nameAr : method.name}</span>
                                                {selectedPaymentMethod === method.id && (
                                                    <i className="bi bi-check-circle-fill text-success check-icon"></i>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* Chargily Redirect Popup */}
                        {showChargilyRedirect && (
                            <div className="success-modal-overlay">
                                <div className="success-modal">
                                    <div className="success-modal-icon">
                                        <div className="spinner-border text-danger" role="status" style={{ width: '3rem', height: '3rem' }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                    <p className="success-modal-message">
                                        {isArabic
                                            ? 'جاري إعادة التوجيه إلى منصة الدفع Chargily...'
                                            : 'Redirection vers la plateforme de paiement Chargily...'}
                                    </p>
                                    <p className="text-muted small mt-2">
                                        {isArabic ? 'يرجى عدم إغلاق هذه الصفحة' : 'Veuillez ne pas fermer cette page'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Save Button */}
                        <div className="d-flex justify-content-center gap-3">
                            <button
                                onClick={handleSave}
                                className="btn btn-primary px-4"
                                disabled={saving || hasPendingPayment || !canSubmit}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        {isArabic ? 'جاري المعالجة...' : 'Traitement...'}
                                    </>
                                ) : (
                                    <>
                                        <i className={`bi ${hasChangedPlan ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'} me-2`}></i>
                                        {hasPendingPayment
                                            ? (isArabic ? 'طلب قيد الانتظار' : 'En attente')
                                            : hasChangedPlan
                                                ? (isArabic ? 'تغيير الاشتراك' : 'Changer de pack')
                                                : (isArabic ? 'تجديد الاشتراك' : 'Renouveler')}
                                    </>
                                )}
                            </button>
                            <Link to={`/${i18n.language}/pricing`} className="btn btn-outline-primary px-4">
                                <i className="bi bi-info-circle me-2"></i>
                                {t('subscription.comparePlans')}
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DashboardSubscription;
